import React, { useEffect, useState, useRef } from "react";

const HOTEL_REVIEW_API = "https://api.hotel.namantechnolab.com/api/hotel/Review";

// ---------------------------------------------------------------------------
// Small inline icons (kept minimal + monochrome so they read as part of the
// system rather than decoration)
// ---------------------------------------------------------------------------
const IconPin = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...props}>
    <path d="M12 22s7-7.58 7-12.5A7 7 0 0 0 5 9.5C5 14.42 12 22 12 22Z" />
    <circle cx="12" cy="9.5" r="2.4" />
  </svg>
);
const IconUser = (props) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...props}>
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c1.2-3.6 4-5.4 7-5.4s5.8 1.8 7 5.4" />
  </svg>
);
const IconMeal = (props) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...props}>
    <path d="M6 3v7a2 2 0 0 0 4 0V3M8 10v11M18 3c-1.7 0-3 2-3 5s1.3 5 3 5M18 3v18" />
  </svg>
);
const IconCheck = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export default function HotelDetail() {
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const [guest, setGuest] = useState({
    title: "Mr",
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+91",
    mobile: "",
    gstEnabled: false,
    billingAddress: "",
    pincode: "",
    state: "Uttar Pradesh",
  });

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchHotelReview();
  }, []);

  const fetchHotelReview = async () => {
    setLoading(true);
    setError(null);

    let searchResponse = null;
    let selectedHotel = null;
    let selectedRoom = null;

    try {
      searchResponse = JSON.parse(sessionStorage.getItem("hotelSearchResponse") || "null");
    } catch (err) {
      console.error("Could not parse hotel search response", err);
    }
    try {
      selectedHotel = JSON.parse(sessionStorage.getItem("selectedHotel") || "null");
    } catch (err) {
      console.error("Could not parse selected hotel", err);
    }
    try {
      selectedRoom = JSON.parse(sessionStorage.getItem("selectedRoomOption") || "null");
    } catch (err) {
      console.error("Could not parse selected room option", err);
    }

    if (!searchResponse || !selectedHotel) {
      setError("Hotel search data not found. Please go back and search again.");
      setLoading(false);
      return;
    }
    if (!selectedRoom) {
      setError("No room was selected. Please go back and choose a room first.");
      setLoading(false);
      return;
    }

    const payload = {
      HotelId: String(selectedHotel.TripId || selectedHotel.HotelId || ""),
      optionId: String(selectedRoom?.optionsId || selectedRoom?.optionsid || selectedRoom?.BookHash || ""),
      HotelSearchres: searchResponse,
    };

    console.log("Hotel review URL:", HOTEL_REVIEW_API);
    console.log("Hotel review request payload:", payload);

    try {
      const res = await fetch(HOTEL_REVIEW_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(`Request failed with status ${res.status}: ${errBody}`);
      }
      const data = await res.json();
      console.log("Hotel review response:", data);
      setReviewData(data);
    } catch (err) {
      console.error("Hotel review request failed:", err);
      setError(err.message || "Something went wrong while fetching hotel review.");
    } finally {
      setLoading(false);
    }
  };

  const updateGuest = (field, value) => setGuest((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <>
        <GlobalStyle />
        <div className="hd-center">
          <div className="hd-spinner" />
          <p className="hd-muted">Fetching your room details…</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <GlobalStyle />
        <div className="hd-center">
          <p className="hd-error">{error}</p>
        </div>
      </>
    );
  }

  if (!reviewData) {
    return (
      <>
        <GlobalStyle />
        <div className="hd-center">
          <p className="hd-muted">No data received.</p>
        </div>
      </>
    );
  }

  const room = Array.isArray(reviewData.HotelRooms) && reviewData.HotelRooms.length > 0 ? reviewData.HotelRooms[0] : null;
  const basePrice = Number(room?.totalPrice || 0);
  const taxesAndFees = 0; // API doesn't send a separate breakup yet; totalPrice is treated as final for now
  const totalAmount = basePrice + taxesAndFees;

  let searchPayload = null;
  try {
    searchPayload = JSON.parse(sessionStorage.getItem("hotelSearchPayload") || "null");
  } catch (err) {
    console.error("Could not parse hotel search payload", err);
  }

  const checkIn = searchPayload?.CheckIn || "—";
  const checkOut = searchPayload?.CheckOut || "—";
  const nights = Number(searchPayload?.Night || 1);
  const adults = room?.Adults || searchPayload?.Numofadults || "—";

  const address = [reviewData.HotelAddress, reviewData.CityName, reviewData.StateName, reviewData.CountryName]
    .filter(Boolean)
    .join(", ");

  // Try, in order: this room's own images -> hotel images from the review
  // response -> images from the earlier DetailSearch response (saved when
  // the user was browsing rooms) -> nothing (monogram fallback renders).
  let detailsResponse = null;
  try {
    detailsResponse = JSON.parse(sessionStorage.getItem("hotelDetailsResponse") || "null");
  } catch (err) {
    console.error("Could not parse stored hotel details response", err);
  }

  const heroImage =
    room?.RommImages?.[0] ||
    (Array.isArray(reviewData.Images) ? reviewData.Images[0] : null) ||
    detailsResponse?.HotelRooms?.[0]?.RommImages?.[0] ||
    (Array.isArray(detailsResponse?.Images) ? detailsResponse.Images[0] : null) ||
    null;

  if (!heroImage) {
    console.log("No image found in Review response or stored DetailSearch response — showing monogram fallback.");
  }
  const initials = (reviewData.hotelname || "H").trim().charAt(0).toUpperCase();
  const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  return (
    <>
      <GlobalStyle />
      <div className="hd-page">
        <div className="hd-shell">
          {/* Step progress */}
          <ol className="hd-steps">
            <li className="hd-step hd-step--done">
              <span className="hd-step-mark"><IconCheck /></span>
              Room
            </li>
            <li className="hd-step hd-step--current">
              <span className="hd-step-mark">2</span>
              Guest details
            </li>
            <li className="hd-step hd-step--upcoming">
              <span className="hd-step-mark">3</span>
              Payment
            </li>
          </ol>

          <div className="hd-grid">
            {/* ---------------- Left column ---------------- */}
            <div className="hd-main">
              {/* Hero property card */}
              <section className="hd-hero">
                <div className="hd-hero-media">
                  {heroImage ? (
                    <img src={heroImage} alt={reviewData.hotelname} />
                  ) : (
                    <div className="hd-hero-fallback">{initials}</div>
                  )}
                  <div className="hd-hero-scrim" />
                  <div className="hd-hero-caption">
                    <span className="hd-hero-rating">
                      {"★".repeat(Math.max(0, Math.min(5, Number(reviewData.HotelRating) || 0)))}
                    </span>
                    <h1 className="hd-hero-name">{reviewData.hotelname}</h1>
                    <p className="hd-hero-address">
                      <IconPin className="hd-icon" /> {address || "Address not available"}
                    </p>
                  </div>
                </div>

                <div className="hd-stay-row">
                  <div className="hd-stay-item">
                    <span className="hd-stay-label">Check-in</span>
                    <span className="hd-stay-value">{checkIn}</span>
                  </div>
                  <div className="hd-stay-divider" />
                  <div className="hd-stay-item">
                    <span className="hd-stay-label">Check-out</span>
                    <span className="hd-stay-value">{checkOut}</span>
                  </div>
                  <div className="hd-stay-divider" />
                  <div className="hd-stay-item">
                    <span className="hd-stay-label">{nights} night{nights > 1 ? "s" : ""}</span>
                    <span className="hd-stay-value">{adults} adult{Number(adults) > 1 ? "s" : ""}</span>
                  </div>
                </div>

                {room && (
                  <div className="hd-room">
                    <span className="hd-room-label">Room selected</span>
                    <h2 className="hd-room-name">{room.RoomType}</h2>
                    <div className="hd-room-meta">
                      <span><IconUser className="hd-icon" /> {room.Adults} adult{Number(room.Adults) > 1 ? "s" : ""}</span>
                      <span><IconMeal className="hd-icon" /> {room.Mealbasis}</span>
                    </div>
                  </div>
                )}
              </section>

              {/* Guest details */}
              <section className="hd-section">
                <h3 className="hd-section-title">Who's staying</h3>
                <p className="hd-section-sub">Your booking voucher and confirmation go to these details.</p>

                <div className="hd-form-row">
                  <div className="hd-field" style={{ maxWidth: 96 }}>
                    <label>Title</label>
                    <select value={guest.title} onChange={(e) => updateGuest("title", e.target.value)}>
                      <option>Mr</option>
                      <option>Mrs</option>
                      <option>Ms</option>
                    </select>
                  </div>
                  <div className="hd-field">
                    <label>First name</label>
                    <input
                      placeholder="e.g. Aarav"
                      value={guest.firstName}
                      onChange={(e) => updateGuest("firstName", e.target.value)}
                    />
                  </div>
                  <div className="hd-field">
                    <label>Last name</label>
                    <input
                      placeholder="e.g. Sharma"
                      value={guest.lastName}
                      onChange={(e) => updateGuest("lastName", e.target.value)}
                    />
                  </div>
                </div>

                <div className="hd-form-row">
                  <div className="hd-field">
                    <label>Email address</label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={guest.email}
                      onChange={(e) => updateGuest("email", e.target.value)}
                    />
                  </div>
                  <div className="hd-field">
                    <label>Mobile number</label>
                    <div className="hd-phone">
                      <select value={guest.countryCode} onChange={(e) => updateGuest("countryCode", e.target.value)}>
                        <option value="+91">+91</option>
                      </select>
                      <input
                        placeholder="10-digit number"
                        value={guest.mobile}
                        onChange={(e) => updateGuest("mobile", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <label className="hd-checkbox">
                  <input
                    type="checkbox"
                    checked={guest.gstEnabled}
                    onChange={(e) => updateGuest("gstEnabled", e.target.checked)}
                  />
                  Add GST details for a business invoice
                </label>

                {guest.gstEnabled && (
                  <div className="hd-form-row hd-form-row--tight">
                    <div className="hd-field">
                      <label>Billing address</label>
                      <input
                        placeholder="Street, area"
                        value={guest.billingAddress}
                        onChange={(e) => updateGuest("billingAddress", e.target.value)}
                      />
                    </div>
                    <div className="hd-field" style={{ maxWidth: 140 }}>
                      <label>Pincode</label>
                      <input
                        placeholder="302017"
                        value={guest.pincode}
                        onChange={(e) => updateGuest("pincode", e.target.value)}
                      />
                    </div>
                    <div className="hd-field" style={{ maxWidth: 200 }}>
                      <label>State</label>
                      <select value={guest.state} onChange={(e) => updateGuest("state", e.target.value)}>
                        <option>Uttar Pradesh</option>
                        <option>Rajasthan</option>
                        <option>Maharashtra</option>
                        <option>Delhi</option>
                      </select>
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* ---------------- Right column ---------------- */}
            <aside className="hd-summary">
              <div className="hd-summary-card">
                <h3 className="hd-summary-title">Price summary</h3>

                <div className="hd-price-row">
                  <span>Room · {nights} night{nights > 1 ? "s" : ""}</span>
                  <span>{formatINR(basePrice)}</span>
                </div>
                <div className="hd-price-row">
                  <span>Taxes &amp; fees</span>
                  <span>{formatINR(taxesAndFees)}</span>
                </div>

                <div className="hd-summary-rule" />

                <div className="hd-price-total">
                  <span>Total payable</span>
                  <span>{formatINR(totalAmount)}</span>
                </div>
                <p className="hd-summary-note">Includes taxes and fees</p>

                <button className="hd-cta">Continue to payment</button>

                <p className="hd-summary-fine">You won't be charged yet</p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles — scoped under .hd-* so this file can drop into an existing app
// without leaking styles onto other pages.
// ---------------------------------------------------------------------------
function GlobalStyle() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');

      .hd-page {
        --ink: #16215C;
        --ink-soft: #26389E;
        --accent: #E4127F;
        --accent-deep: #B80E64;
        --paper: #F4F6FB;
        --card: #FFFFFF;
        --line: #E1E5F2;
        --text: #1A1F36;
        --text-soft: #667085;
        --danger: #B3261E;

        background: var(--paper);
        min-height: 100vh;
        padding: 40px 20px 80px;
        font-family: 'Inter', sans-serif;
        color: var(--text);
      }

      .hd-center {
        min-height: 60vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-family: 'Inter', sans-serif;
      }
      .hd-muted { color: #756F63; }
      .hd-error { color: #B3261E; font-weight: 600; }
      .hd-spinner {
        width: 28px; height: 28px; border-radius: 50%;
        border: 3px solid #E1E5F2; border-top-color: #E4127F;
        animation: hd-spin 0.8s linear infinite;
      }
      @keyframes hd-spin { to { transform: rotate(360deg); } }

      .hd-shell { max-width: 1080px; margin: 0 auto; }

      /* Steps */
      .hd-steps {
        display: flex; align-items: center; gap: 10px;
        list-style: none; padding: 0; margin: 0 0 28px;
        font-size: 13px; color: var(--text-soft);
      }
      .hd-step { display: flex; align-items: center; gap: 8px; }
      .hd-step:not(:last-child)::after {
        content: ""; width: 28px; height: 1px; background: var(--line); margin-left: 10px;
      }
      .hd-step-mark {
        width: 20px; height: 20px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 11px; border: 1px solid var(--line); color: var(--text-soft);
      }
      .hd-step--done .hd-step-mark { background: linear-gradient(90deg, #0d2b6b 0%, #1a4fa0 55%, #2a63c4 100%); border-color: var(--ink); color: #fff; }
      .hd-step--done { color: var(--ink); }
      .hd-step--current .hd-step-mark { border-color: var(--accent); color: var(--accent-deep); font-weight: 600; }
      .hd-step--current { color: var(--text); font-weight: 600; }

      /* Grid */
      .hd-grid { display: grid; grid-template-columns: 1.7fr 1fr; gap: 24px; align-items: start; }
      @media (max-width: 900px) { .hd-grid { grid-template-columns: 1fr; } }

      .hd-main { display: flex; flex-direction: column; gap: 20px; }

      /* Hero */
      .hd-hero {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: 14px;
        overflow: hidden;
      }
      .hd-hero-media { position: relative; height: 230px; }
      .hd-hero-media img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .hd-hero-fallback {
        width: 100%; height: 100%;
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, var(--ink), var(--ink-soft));
        color: var(--accent); font-family: 'Fraunces', serif; font-size: 64px; font-weight: 500;
      }
      .hd-hero-scrim {
        position: absolute; inset: 0;
        background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(10,20,16,0.82) 100%);
      }
      .hd-hero-caption { position: absolute; left: 20px; right: 20px; bottom: 16px; color: #fff; }
      .hd-hero-rating { color: var(--accent); font-size: 13px; letter-spacing: 1px; }
      .hd-hero-name {
        font-family: 'Fraunces', serif; font-weight: 600; font-size: 26px;
        margin: 2px 0 6px; line-height: 1.15;
      }
      .hd-hero-address {
        display: flex; align-items: center; gap: 6px;
        font-size: 13px; color: rgba(255,255,255,0.88); margin: 0;
      }
      .hd-icon { flex-shrink: 0; opacity: 0.8; }

      .hd-stay-row {
        display: flex; align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid var(--line);
      }
      .hd-stay-item { display: flex; flex-direction: column; gap: 3px; }
      .hd-stay-label { font-size: 11px; color: var(--text-soft); }
      .hd-stay-value { font-size: 14px; font-weight: 600; }
      .hd-stay-divider { width: 1px; height: 28px; background: var(--line); margin: 0 22px; }

      .hd-room { padding: 18px 20px 22px; }
      .hd-room-label { font-size: 12px; color: var(--text-soft); }
      .hd-room-name {
        font-family: 'Fraunces', serif; font-weight: 600; font-size: 19px;
        margin: 4px 0 8px;
      }
      .hd-room-meta { display: flex; gap: 18px; font-size: 13px; color: var(--text-soft); }
      .hd-room-meta span { display: flex; align-items: center; gap: 6px; }

      /* Section (guest details) */
      .hd-section {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: 14px;
        padding: 24px 24px 28px;
      }
      .hd-section-title { font-family: 'Fraunces', serif; font-size: 20px; font-weight: 600; margin: 0 0 4px; }
      .hd-section-sub { font-size: 13px; color: var(--text-soft); margin: 0 0 20px; }

      .hd-form-row { display: flex; gap: 18px; margin-bottom: 18px; }
      .hd-form-row--tight { margin-top: -4px; }
      @media (max-width: 560px) { .hd-form-row { flex-direction: column; gap: 14px; } }

      .hd-field { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .hd-field label { font-size: 12px; color: var(--text-soft); }
      .hd-field input, .hd-field select {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        padding: 8px 2px;
        border: none;
        border-bottom: 1.5px solid var(--line);
        background: transparent;
        color: var(--text);
        outline: none;
        transition: border-color 0.15s ease;
      }
      .hd-field input::placeholder { color: #B7B0A2; }
      .hd-field input:focus, .hd-field select:focus { border-bottom-color: var(--accent); }

      .hd-phone { display: flex; gap: 10px; }
      .hd-phone select { width: 62px; flex-shrink: 0; }
      .hd-phone input { flex: 1; min-width: 0; }

      .hd-checkbox {
        display: flex; align-items: center; gap: 9px;
        font-size: 13.5px; color: var(--text);
        margin-top: 4px; cursor: pointer;
      }
      .hd-checkbox input { accent-color: var(--accent); width: 15px; height: 15px; }

      /* Price summary */
      .hd-summary { position: sticky; top: 24px; }
      @media (max-width: 900px) { .hd-summary { position: static; } }

      .hd-summary-card {
        background: #ffffff;
        color: #000000;
        border-radius: 14px;
        padding: 24px;
      }
      .hd-summary-title { font-family: 'Fraunces', serif; font-size: 18px; font-weight: 600; margin: 0 0 16px; }

      .hd-price-row {
        display: flex; justify-content: space-between;
        font-size: 13.5px; 
        color: #000000;
        margin-bottom: 10px;
      }

      .hd-summary-rule { height: 1px; background: rgba(242,239,230,0.18); margin: 14px 0; }

      .hd-price-total {
        display: flex; justify-content: space-between; align-items: baseline;
        font-family: 'Fraunces', serif; font-size: 22px; font-weight: 600;
        color:#ea580c;
      }
      .hd-summary-note { font-size: 11.5px; color: #000000; margin: 4px 0 0; }

      .hd-cta {
        width: 100%; margin-top: 20px;
        background: #ea580c;
        color: #FFFFFF;
        border: none; border-radius: 8px;
        padding: 13px 0;
        font-size: 14.5px; font-weight: 700;
        font-family: 'Inter', sans-serif;
        cursor: pointer;
        transition: background 0.15s ease;
      }
      .hd-cta:hover { background: var(--accent-deep); }

      .hd-summary-fine { text-align: center; font-size: 11.5px; color: #000000; margin: 10px 0 0; }
    `}</style>
  );
}